import React from 'react'

import { getNodeBox, getNodePosition } from '@/views/VoiceAgents/canvasHelpers'

// SVG edge layer — bezier paths between node anchor points, plus the
// dashed peer-handoff overlay synthesised from `peerEdges` (passed in by
// CanvasView). No midpoint badges: the edge stroke color + dash pattern +
// arrow direction carry the category signal on their own.
//
// `source` / `target` are `{ position, node }` records (built by the caller
// from agent.nodes + positionsByNodeId). The per-node `node.type` is used
// to look up the rendered box height — short utility nodes (entry /
// greeting / exit) render at ~96px tall, so anchoring at the layout
// budget's 200/2=100 puts the arrow tip below the visible card.
const computeAnchorPoints = (source, target) => {
  const sourceBox = getNodeBox(source.node.type)
  const targetBox = getNodeBox(target.node.type)
  const sourceCenterX = source.position.x + sourceBox.width / 2
  const targetCenterX = target.position.x + targetBox.width / 2
  const goingRight = targetCenterX >= sourceCenterX
  return {
    x1: goingRight ? source.position.x + sourceBox.width : source.position.x,
    y1: source.position.y + sourceBox.height / 2,
    x2: goingRight ? target.position.x : target.position.x + targetBox.width,
    y2: target.position.y + targetBox.height / 2,
    goingRight,
  }
}

// Cubic-bezier path between two anchor points. The control points need to
// pull AWAY from each anchor in the direction of travel — when going right,
// control 1 sits to the right of x1 and control 2 sits to the left of x2;
// when going LEFT (back-edges like a `success` edge returning from a tool to
// its caller), they need to mirror — otherwise the curve loops over its own
// endpoints and visually crosses the source/target nodes.
const bezierD = (x1, y1, x2, y2, goingRight) => {
  const dx = Math.max(40, Math.abs(x2 - x1) * 0.5)
  if (goingRight) {
    return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`
  }
  return `M ${x1} ${y1} C ${x1 - dx} ${y1}, ${x2 + dx} ${y2}, ${x2} ${y2}`
}

const CanvasEdges = ({ agent, positionsByNodeId, selectedNodeId, peerEdges }) => {
  if (!agent || !Array.isArray(agent.nodes) || !Array.isArray(agent.edges)) {
    return null
  }

  const nodeById = {}
  agent.nodes.forEach((node, index) => {
    nodeById[node.id] = {
      node,
      position: positionsByNodeId[node.id] || getNodePosition(node, index),
    }
  })

  return (
    <svg className="voice-agents__edges-svg" data-testid="canvas-edges">
      <defs>
        <marker
          id="prompt-ext-arrow"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--color-neutral)" />
        </marker>
        <marker
          id="prompt-ext-arrow-highlight"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--color-brand)" />
        </marker>
        <marker
          id="prompt-ext-arrow-return"
          viewBox="0 0 10 10"
          refX="2"
          refY="5"
          markerWidth="5"
          markerHeight="5"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--color-neutral)" fillOpacity="0.7" />
        </marker>
        <marker
          id="prompt-ext-arrow-peer"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="7"
          markerHeight="7"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--color-brand)" fillOpacity="0.95" />
        </marker>
      </defs>

      {/* Primary edges */}
      {agent.edges.map((edge) => {
        const source = nodeById[edge.from]
        const target = nodeById[edge.to]
        if (!source || !target) {
          return null
        }
        const { x1, y1, x2, y2, goingRight } = computeAnchorPoints(source, target)
        const category = edge.edgeCategory || 'default'

        const isHighlighted = edge.from === selectedNodeId || edge.to === selectedNodeId
        const isConditional = category === 'routing-decision' || category === 'failure'
        const edgeKey = `${edge.from}-${edge.to}-${category}`

        return (
          <path
            key={edgeKey}
            d={bezierD(x1, y1, x2, y2, goingRight)}
            className="voice-agents__edge-path"
            fill="none"
            stroke={isHighlighted ? 'var(--color-brand)' : 'var(--color-neutral)'}
            strokeWidth={isHighlighted ? 2.5 : 1.6}
            strokeDasharray={isConditional ? '6 4' : undefined}
            markerEnd={`url(#${isHighlighted ? 'prompt-ext-arrow-highlight' : 'prompt-ext-arrow'})`}
            markerStart={edge.bidirectional ? 'url(#prompt-ext-arrow-return)' : undefined}
            data-testid={`edge-path-${edge.from}-${edge.to}`}
            data-edge-category={category}
            data-bidirectional={edge.bidirectional ? 'true' : 'false'}
          />
        )
      })}

      {/* Peer-handoff overlay (dashed blurple, only when a sub-agent is selected) */}
      {peerEdges &&
        peerEdges.map((peer) => {
          const source = nodeById[peer.from]
          const target = nodeById[peer.to]
          if (!source || !target) {
            return null
          }
          const { x1, y1, x2, y2, goingRight } = computeAnchorPoints(source, target)
          return (
            <path
              key={`peer-${peer.from}-${peer.to}`}
              d={bezierD(x1, y1, x2, y2, goingRight)}
              fill="none"
              stroke="var(--color-brand)"
              strokeWidth={2}
              strokeOpacity={0.95}
              strokeDasharray="7 4"
              markerEnd="url(#prompt-ext-arrow-peer)"
              data-testid={`peer-path-${peer.from}-${peer.to}`}
            />
          )
        })}
    </svg>
  )
}

export default CanvasEdges
